/**
 * Bag slot accounting — the one rule for "does this fit?" (plan 06, 2026-10-07).
 *
 * The bag (and the bank vault) store one entry per item id. An entry's
 * quantity may exceed the item's stack limit: it then occupies
 * `ceil(quantity / maxStack)` slots, exactly as if it were split into full
 * stacks. So two Cloth Gloves (stack limit 1) are one entry of quantity 2 and
 * take two slots, and 120 potions (limit 99) take two.
 *
 * Every path that adds to a bag (quest rewards, loot pickup, shop purchases,
 * unequip / equipment swap, bank deposit and withdraw) asks
 * `planBagChange` first and refuses with a named reason when it doesn't fit.
 * The client uses the same functions to show "N / 50 slots" and to explain a
 * blocked quest turn-in before the click.
 *
 * Shared leaf module: no game/ or server/ imports.
 */

import { ITEM_DATABASE } from '../ItemData';
import { GAMEPLAY_CONFIG } from '../constants/GameplayConfig';

/** Bag size when a player record carries none (`Player.maxInventorySlots`). */
export const DEFAULT_BAG_SLOTS = 50;
/** Bank vault size when a player record carries none (`bankMaxSlots`). */
export const DEFAULT_VAULT_SLOTS = 100;

export interface BagStack {
  quantity: number;
  maxStack?: number;
}

/** A bag keyed by item id: a Map, a Colyseus MapSchema, or anything shaped like one. */
export interface BagLike<T extends BagStack = BagStack> {
  get(id: string): T | undefined;
  forEach(callback: (value: T, key: string) => void): void;
}

export interface BagAddition {
  id: string;
  quantity: number;
  /** Stack limit to use when neither the bag entry nor the item database has one. */
  maxStack?: number;
}

export interface BagRemoval {
  id: string;
  quantity: number;
}

export type BagPlan =
  | { ok: true; usedSlots: number; maxSlots: number }
  | {
    ok: false;
    reason: 'bag_full' | 'invalid_quantity';
    usedSlots: number;
    maxSlots: number;
    /** Slots the change would need beyond what is free. */
    missingSlots: number;
    /** Item ids whose addition needs a slot the bag doesn't have. */
    blockingItemIds: string[];
  };

/** Stack limit for an item: the stored entry wins, then the hint, then the item database. */
export function resolveMaxStack(itemId: string, stack?: { maxStack?: number }, hint?: number): number {
  const candidate = stack?.maxStack || hint
    || (ITEM_DATABASE as Record<string, { maxStack?: number } | undefined>)[itemId]?.maxStack
    || GAMEPLAY_CONFIG.items.defaultMaxStack;
  return Number.isFinite(candidate) && candidate >= 1 ? Math.floor(candidate) : 1;
}

/** Slots one entry occupies. */
export function slotsForQuantity(quantity: number, maxStack: number): number {
  if (!(quantity > 0)) return 0;
  return Math.ceil(quantity / Math.max(1, maxStack));
}

/** Slots the whole bag occupies. */
export function countUsedSlots(bag: BagLike | undefined | null): number {
  if (!bag) return 0;
  let used = 0;
  bag.forEach((stack, id) => {
    used += slotsForQuantity(stack?.quantity ?? 0, resolveMaxStack(id, stack));
  });
  return used;
}

/**
 * Simulate removals then additions against a bag without touching it.
 * Additions are applied in order; the first one that pushes the bag past
 * `maxSlots` and every later one that still needs room are reported.
 */
export function planBagChange(
  bag: BagLike | undefined | null,
  change: { add?: readonly BagAddition[]; remove?: readonly BagRemoval[] },
  maxSlots: number,
): BagPlan {
  const projected = new Map<string, { quantity: number; maxStack: number }>();
  const read = (id: string, hint?: number) => {
    let entry = projected.get(id);
    if (!entry) {
      const stored = bag?.get(id);
      entry = { quantity: stored?.quantity ?? 0, maxStack: resolveMaxStack(id, stored, hint) };
      projected.set(id, entry);
    }
    return entry;
  };
  let used = countUsedSlots(bag);
  const slotsOf = (entry: { quantity: number; maxStack: number }) => slotsForQuantity(entry.quantity, entry.maxStack);

  for (const removal of change.remove ?? []) {
    const entry = read(removal.id);
    const before = slotsOf(entry);
    entry.quantity = Math.max(0, entry.quantity - Math.max(0, removal.quantity));
    used -= before - slotsOf(entry);
  }

  const blockingItemIds: string[] = [];
  let missingSlots = 0;
  for (const addition of change.add ?? []) {
    if (!addition.id) continue;
    if (!Number.isFinite(addition.quantity) || addition.quantity <= 0) {
      return { ok: false, reason: 'invalid_quantity', usedSlots: used, maxSlots, missingSlots: 0, blockingItemIds: [addition.id] };
    }
    const entry = read(addition.id, addition.maxStack);
    const before = slotsOf(entry);
    entry.quantity += addition.quantity;
    const grow = slotsOf(entry) - before;
    if (used + grow > maxSlots) {
      missingSlots += used + grow - Math.max(used, maxSlots);
      if (!blockingItemIds.includes(addition.id)) blockingItemIds.push(addition.id);
    }
    used += grow;
  }

  if (blockingItemIds.length > 0) {
    return { ok: false, reason: 'bag_full', usedSlots: countUsedSlots(bag), maxSlots, missingSlots, blockingItemIds };
  }
  return { ok: true, usedSlots: used, maxSlots };
}

/** The bag size a player record carries. */
export function bagMaxSlots(player: { maxInventorySlots?: number } | undefined | null): number {
  const value = player?.maxInventorySlots;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : DEFAULT_BAG_SLOTS;
}
